// Application configuration settings

// API URL based on environment
export const API_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:5002';

// Other application constants
export const APP_NAME = 'SCAD Project Management';
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const SUPPORTED_FILE_TYPES = [
  'image/jpeg', 
  'image/png', 
  'application/pdf', 
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

export const FEEDBACK_EMAIL =
  process.env.REACT_APP_FEEDBACK_EMAIL ||
  'nathan.mcconnell@sandboxentmt.com';

// Feature flags
export const FEATURE_FLAGS = {
  storyboardCG: false,
  storyboardAI: false,        // AI generation in storyboard add/edit frame
  storyboardAssets: false,    // Asset library in storyboard add/edit frame
  resourcesLightingDiagram: true,
  messagesVideoCall: false,
  scheduleDayForDays: true
};

// Hidden UI elements (keep list updated when hiding features)
export const HIDDEN_UI_ELEMENTS = [
  {
    id: 'storyboard.cgSceneButton',
    description: 'Storyboard Edit Frame dialog – CG Scene generator card/button',
    reason: 'Feature still in development'
  },
  {
    id: 'resources.lightingDiagramTab',
    description: 'Resources → Lighting Diagram tab/tool',
    reason: 'Lighting diagram tool still in development'
  },
  {
    id: 'schedule.dayForDays',
    description: 'Schedule tab → Day-for-Days tracker',
    reason: 'Day-for-Days pipeline is still in active development'
  },
  {
    id: 'scripts.screenplayWritingTab',
    description: 'Scripts module → Screenplay Writing tab',
    reason: 'Screenplay writing experience temporarily disabled'
  },
  {
    id: 'messages.videoCallButton',
    description: 'Messages view → video call button',
    reason: 'Video calling temporarily disabled'
  }
];

// Upcoming features surfaced in the notification center
export const UPCOMING_FEATURES = [
  {
    id: 'scripts.hollywoodFormatting',
    title: 'Hollywood-Standard Screenplay Formatting',
    status: 'Now Available',
    description: 'Industry-standard margins (1.5" left, 1" right/top/bottom), Courier 12pt, page numbers at 0.5" from top, scene numbers on both sides of sluglines, (MORE)/(CONT\'D) dialogue splitting, CONTINUED: markers, and proper title page layout.'
  },
  {
    id: 'scripts.pdfExport',
    title: 'Screenplay PDF Export',
    status: 'Now Available',
    description: 'Export Fountain screenplays to professionally formatted PDFs with jsPDF — correct margins, indents, pagination, scene numbers, and (MORE)/(CONT\'D) dialogue splitting baked in.'
  },
  {
    id: 'storytools.storyToolsUpdate',
    title: 'Story Tools Update',
    status: 'Now Available',
    description: 'Timeline redesign with era manager, mouse wheel zoom, drag optimization. World-building persistence for all components. MindMap and IndexCards optimizations. Storyboard enhancements.'
  },
  {
    id: 'scripts.pdfAnalysis',
    title: 'PDF Screenplay Analysis',
    status: 'Now Available',
    description: 'Analyze PDF screenplays with AI-powered coverage reports, character breakdowns, and market analysis.'
  },
  {
    id: 'oneliner.characterFilter',
    title: 'Improved Character Filtering',
    status: 'Now Available',
    description: 'Case-insensitive character name filtering—"NURA" and "Nura" are now treated as the same character.'
  },
  {
    id: 'oneliner.sceneEditPersistence',
    title: 'Scene Edit Persistence',
    status: 'Now Available',
    description: 'Character removals and scene edits now persist to the backend after saving.'
  },
  {
    id: 'callsheet.compactHeader',
    title: 'Compact Callsheet Header',
    status: 'Now Available',
    description: 'Reduced callsheet header height for a cleaner, more compact layout.'
  },
  {
    id: 'project.instantNameSync',
    title: 'Instant Project Name Sync',
    status: 'Now Available',
    description: 'Project name changes now update instantly across Pitch Deck and Callsheet without refresh.'
  },
  {
    id: 'feedback.reportIssuesButton',
    title: 'Report Issues Button',
    status: 'Now Available',
    description: 'Quickly send bugs or blockers straight from the notification center.'
  },
  {
    id: 'storyboard.aiGeneration',
    title: 'Storyboard AI Image Generation',
    status: 'In QA',
    description: 'Qwen-powered z-image and composite generation in the storyboard frame editor. Generate shots from prompts, reference images, and composition grids. Code complete — enabling for production soon.'
  },
  {
    id: 'storyboard.assetLibrary',
    title: 'Storyboard Asset Library',
    status: 'In QA',
    description: 'Browse and insert project assets directly into storyboard frames. "Use in frame" workflow now works from anywhere — no need to open the edit dialog first. Code complete — enabling for production soon.'
  },
  {
    id: 'messages.videoCall',
    title: 'Video Chat & Calls',
    status: 'In Development',
    description: 'High-fidelity voice/video calling directly inside Messages.'
  },
  {
    id: 'storyboard.cgScene',
    title: 'CG Storyboard Generator',
    status: 'In Development',
    description: 'Block out scenes with AI-powered camera and lighting setups.'
  },
  {
    id: 'schedule.dayForDays',
    title: 'Day-for-Days Tracker',
    status: 'Work in Progress',
    description: 'Actor day-count automation is still being tuned—full release coming after QA completes.'
  },
  {
    id: 'studio.aiDirector',
    title: 'AI Director Studio',
    status: 'Research',
    description: 'AI-generated storyboard & video workstation (model-agnostic drivers coming soon).'
  },
  {
    id: 'resources.lightDiagram',
    title: 'Lighting Diagram Tool',
    status: 'Design Review',
    description: 'Plan studio lighting layouts with shareable diagrams.'
  },
  {
    id: 'scripts.screenplayWriting',
    title: 'Screenplay Writing Workspace',
    status: 'In QA',
    description: 'Collaborative script editor with Hollywood-standard formatting, scene numbering, dual dialogue, and PDF export. Formatting engine complete — writing workspace UI undergoing final QA.'
  }
];

// Date formats
export const DATE_FORMAT = 'MM/dd/yyyy';
export const TIME_FORMAT = 'h:mm a';
export const DATETIME_FORMAT = 'MM/dd/yyyy h:mm a';

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'token',
  USER: 'user',
  THEME_PREFERENCE: 'theme',
  LAST_PROJECT: 'lastProject'
};

// Default pagination options
export const PAGINATION_OPTIONS = [5, 10, 25, 50, 100];
